'use client';

/**
 * HeroMarketing — main hero section for the marketing home page.
 * Headline + 2 CTAs + ambient gradient orb + animated stats counter.
 *
 * @spec /doc/specs/frontend/design-system/design-tokens-v2.md
 * @userStory US-EP022-015
 *
 * Note: video background is OFF in S39 (waiting on US-EP022-013 ffmpeg pipeline
 * to convert the PPTX GIFs). Once the videos exist in /public/videos/marketing/,
 * uncomment the <VideoBackground /> below.
 */

import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { ArrowRight, PlayCircle } from 'lucide-react';
import { Link } from '@/i18n/navigation';
import { Button } from '@/components/marketing/ui/button';
import { EyebrowBadge } from '@/components/marketing/ui/eyebrow';
import { GradientOrb } from '@/components/marketing/ui/gradient-orb';
import { Section } from '@/components/marketing/ui/section';
import { VideoBackground } from '@/components/marketing/ui/video-background';

const STATS: Array<{ key: string; value: number; suffix: string; prefix?: string }> = [
  { key: 'noClick', value: 60, suffix: '%' },
  { key: 'adoption', value: 400, suffix: '%' },
  { key: 'consumers', value: 47, suffix: '%' },
  { key: 'trafficDrop', value: 22, suffix: '%', prefix: '-' },
];

function StatCounter({
  value,
  suffix,
  prefix = '',
}: {
  value: number;
  suffix: string;
  prefix?: string;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const reduce = useReducedMotion();
  // Render the final value on SSR + first client render to avoid hydration
  // mismatch. Switch to animated counting only after mount, when we know
  // whether the user prefers reduced motion and whether the element is in view.
  const [mounted, setMounted] = useState(false);
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    if (reduce) {
      setDisplay(value);
      return;
    }
    if (!inView) {
      setDisplay(0);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const duration = 1200;
    const tick = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(value * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mounted, inView, value, reduce]);

  return (
    <span ref={ref} className="text-display text-3xl font-bold text-cyan-300 lg:text-4xl">
      {prefix}
      {display}
      {suffix}
    </span>
  );
}

interface HeroMarketingProps {
  /** When true (server-detected crawler), strip Framer Motion + VideoBackground
   * so the SSR HTML is fully visible to Google's headless renderer (no animations
   * that hide content, no <video> element that paints over the text). */
  isCrawler?: boolean;
}

export function HeroMarketing({ isCrawler = false }: HeroMarketingProps = {}) {
  const t = useTranslations('marketing.home.hero');

  return (
    <Section bleed padded={false} className="relative isolate overflow-hidden bg-ink-950">
      {/* Ambient video background + decorative orbs — only for human visitors.
          Crawlers (Googlebot WRS, Bingbot, GPTBot, etc.) get a flat dark hero
          so the screenshot tool can never paint blurred orbs / video frames
          over the text. */}
      {!isCrawler && (
        <>
          <VideoBackground
            srcWebm="/videos/marketing/loop-hero-1.webm"
            srcMp4="/videos/marketing/loop-hero-1.mp4"
            poster="/videos/marketing/loop-hero-1.jpg"
            overlayOpacity={0.2}
          />
          <GradientOrb position="top-right" size="xl" intensity="medium" color="cyan" />
          <GradientOrb position="bottom-left" size="lg" intensity="low" color="cyan" />
        </>
      )}

      {/* For crawlers we drop min-h-[calc(100vh-4rem)] + justify-center so the
          content starts at the top of the section (Google's mobile screenshot
          tool only captures the first viewport — vertically-centered content
          can land below the crop). */}
      <div
        className={
          isCrawler
            ? 'relative z-10 mx-auto max-w-[1280px] px-4 pb-16 pt-10 sm:px-6 lg:px-8'
            : 'relative z-10 mx-auto flex min-h-[calc(100vh-4rem)] max-w-[1280px] flex-col justify-center px-4 pb-24 pt-20 sm:px-6 lg:px-8 lg:pb-32 lg:pt-28'
        }
      >
        {/* initial={false} — skip entrance animation so SSR/Googlebot screenshot
            sees the hero content visible. The framer-motion entrance was making
            the entire <h1>+CTAs invisible (opacity:0) until JS hydrated, which
            broke Search Console "Test live" rendering (black hero). */}
        <motion.div
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
          className="max-w-4xl"
        >
          <EyebrowBadge>{t('eyebrow')}</EyebrowBadge>

          <h1 className="text-display mt-6 text-balance text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl xl:text-7xl">
            {t('title')}
          </h1>

          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/75 lg:text-xl">
            {t('subtitle')}
          </p>

          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Button
              as={Link}
              href="/contact"
              variant="primary"
              size="lg"
              iconRight={<ArrowRight size={18} />}
            >
              {t('ctaPrimary')}
            </Button>
            <Button
              as={Link}
              href="/methodology"
              variant="secondary"
              size="lg"
              iconLeft={<PlayCircle size={18} />}
            >
              {t('ctaSecondary')}
            </Button>
          </div>
        </motion.div>

        {/* Stats grid */}
        <div className="mt-16 grid max-w-5xl grid-cols-2 gap-6 lg:mt-24 lg:grid-cols-4 lg:gap-8">
          {STATS.map((stat) => (
            <div key={stat.key} className="border-l-2 border-cyan-500/30 pl-4">
              <StatCounter value={stat.value} suffix={stat.suffix} prefix={stat.prefix} />
              <p className="mt-2 text-xs leading-relaxed text-white/60 lg:text-sm">
                {t(`stats.${stat.key}`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}
